// src/components/dashboard/AggregateScoreOrbit.tsx
import React, { useRef, useEffect } from 'react';

interface AggregateScoreOrbitProps {
  score: number | null;
  isSkeleton?: boolean;
}

interface Particle {
  angle: number;
  radius: number;
  baseRadius: number;
  baseOpacity: number;
  speed: number;
  size: number;
  opacity: number;
  color: string;
  noiseOffsetX: number;
  noiseOffsetY: number;
  noiseSpeed: number;
  phaseOffset: number;
  currentX: number;
  currentY: number;
}

export const AggregateScoreOrbit: React.FC<AggregateScoreOrbitProps> = ({ score, isSkeleton = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const timeRef = useRef<number>(0);

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'EXCELLENT';
    if (score >= 75) return 'GOOD';
    if (score >= 60) return 'FAIR';
    if (score >= 40) return 'NEEDS ATTENTION';
    return 'POOR';
  };

  const getScoreDate = () => {
    const now = new Date();
    const month = now.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const day = now.getDate();
    const year = now.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  const getColorsForScore = (score: number) => {
    if (score >= 80) {
      // Excellent: Vibrant rainbow of cool tones
      return ['#1dd1a1', '#10ac84', '#00d2d3', '#48dbfb', '#0abde3', '#54a0ff', '#2e86de', '#5f27cd', '#341f97'];
    } else if (score >= 60) {
      // Good: Blue-Green shades
      return ['#2b8bc1', '#3dc56d'];
    } else if (score >= 40) {
      // Fair: Yellow-Orange shades
      return ['#f5a623', '#f7b731'];
    } else {
      // Poor: Red-Orange shades
      return ['#e74c3c', '#f39c12'];
    }
  };

  const createParticle = (scoreColors: string[]): Particle => {
    // Get responsive radius based on canvas size
    const canvas = canvasRef.current;
    const containerWidth = canvas?.parentElement?.clientWidth || 500;
    const size = Math.min(containerWidth * 1, 450); // Reduced from 600 to 450
    const baseRadiusCalc = size * 0.35; // Increased for larger orbit
    const variation = size * 0.08; // Reduced from 12% to 8%

    const particleBaseRadius = baseRadiusCalc + (Math.random() - 0.5) * variation;
    const particleBaseOpacity = Math.random() * 0.1 + 0.9;

    return {
      angle: Math.random() * Math.PI * 2,
      radius: particleBaseRadius,
      baseRadius: particleBaseRadius,
      baseOpacity: particleBaseOpacity,
      speed: 0.0008 + Math.random() * 0.003,
      size: Math.random() * 1.5 + 0.4,
      opacity: particleBaseOpacity,
      color: scoreColors[Math.floor(Math.random() * scoreColors.length)],
      noiseOffsetX: Math.random() * 1000,
      noiseOffsetY: Math.random() * 1000,
      noiseSpeed: 0.0008 + Math.random() * 0.0015,
      phaseOffset: Math.random() * Math.PI * 2,
      currentX: 0,
      currentY: 0,
    };
  };

  const updateParticle = (particle: Particle, centerX: number, centerY: number, time: number) => {
    // Pulse animation: particles breathe in/out instead of rotating
    // Each particle has its own phase offset for organic variation
    const heartbeat = Math.sin(time * 0.01 + particle.phaseOffset);
    const pulse = heartbeat; // Range: -1 to 1

    // Apply pulse to radius (25 = pulse amplitude)
    particle.radius = particle.baseRadius + pulse * 25;

    // Pulse opacity for extra effect
    particle.opacity = particle.baseOpacity * (0.7 + Math.abs(pulse) * 0.3);

    const wobbleX =
      Math.sin(particle.noiseOffsetX + particle.phaseOffset) * 15 +
      Math.sin(particle.noiseOffsetX * 0.4) * 8 +
      Math.sin(particle.noiseOffsetX * 1.7) * 3;

    const wobbleY =
      Math.cos(particle.noiseOffsetY + particle.phaseOffset) * 15 +
      Math.cos(particle.noiseOffsetY * 0.6) * 8 +
      Math.cos(particle.noiseOffsetY * 1.5) * 3;

    // Angle is now fixed (no rotation), particles stay at their initial position
    particle.currentX = centerX + Math.cos(particle.angle) * particle.radius + wobbleX;
    particle.currentY = centerY + Math.sin(particle.angle) * particle.radius + wobbleY;

    particle.noiseOffsetX += particle.noiseSpeed;
    particle.noiseOffsetY += particle.noiseSpeed;
  };

  const drawParticle = (ctx: CanvasRenderingContext2D, particle: Particle) => {
    ctx.beginPath();
    ctx.arc(particle.currentX, particle.currentY, particle.size, 0, Math.PI * 2);
    ctx.fillStyle = particle.color;
    ctx.globalAlpha = particle.opacity;
    ctx.fill();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Responsive canvas size based on container
    const containerWidth = canvas.parentElement?.clientWidth || 500;
    const size = Math.min(containerWidth * 1, 450); // Reduced from 600 to 450

    canvas.width = size * 2;
    canvas.height = size * 2;
    ctx.scale(2, 2);

    const centerX = size / 2;
    const centerY = size / 2;

    // Initialize particles
    const healthScore = score || 65;
    const scoreColors = getColorsForScore(healthScore);
    particlesRef.current = Array.from({ length: 4500 }, () => createParticle(scoreColors)); // Increased particle density

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, size, size);

      // Increment time for pulse animation
      timeRef.current += 1;

      particlesRef.current.forEach(particle => {
        updateParticle(particle, centerX, centerY, timeRef.current);
        drawParticle(ctx, particle);
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [score]);

  // Show blank state if no data
  if (score === null && !isSkeleton) {
    return (
      <div style={{
        backgroundColor: 'var(--color-card-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: '16px',
        padding: 'clamp(16px, 2vw, 24px)',
        paddingBottom: 'clamp(12px, 1.5vw, 16px)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        minHeight: 'clamp(450px, 50vh, 650px)',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
      }}>
        {/* Header */}
        <div style={{
          marginBottom: 'var(--space-4)',
          zIndex: 10
        }}>
          <h3 style={{
            fontFamily: 'var(--font-primary)',
            fontSize: '11px',
            fontWeight: 'var(--font-medium)',
            color: 'var(--color-muted)',
            margin: 0,
            letterSpacing: '0.8px',
            textTransform: 'uppercase'
          }}>
            AGGREGATE SCORE
          </h3>
        </div>

        {/* Empty State */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          color: 'var(--color-muted)'
        }}>
          <div style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'clamp(14px, 1.2vw, 16px)',
            fontWeight: 'var(--font-medium)',
            textAlign: 'center',
            color: 'var(--color-secondary)'
          }}>
            No health data yet
          </div>
          <div style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'clamp(12px, 1vw, 14px)',
            fontWeight: 'var(--font-normal)',
            textAlign: 'center',
            color: 'var(--color-muted)',
            maxWidth: '300px'
          }}>
            Upload your first lab report to see your aggregate health score
          </div>
        </div>
      </div>
    );
  }

  // Effect for skeleton colors
  useEffect(() => {
    if (isSkeleton) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const size = Math.min(canvas.parentElement?.clientWidth || 500, 450);
      const centerX = size / 2;
      const centerY = size / 2;

      // Subtle grayscale/muted colors for skeleton
      const skeletonColors = ['rgba(200, 200, 200, 0.2)', 'rgba(220, 220, 220, 0.1)'];
      particlesRef.current = Array.from({ length: 1500 }, () => {
        const p = createParticle(skeletonColors);
        p.speed = 0.0002; // Very slow
        return p;
      });

      const animateSkeleton = () => {
        ctx.clearRect(0, 0, size, size);
        timeRef.current += 0.5;
        particlesRef.current.forEach(particle => {
          updateParticle(particle, centerX, centerY, timeRef.current);
          drawParticle(ctx, particle);
        });
        animationFrameRef.current = requestAnimationFrame(animateSkeleton);
      };

      animateSkeleton();
      return () => {
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      };
    }
  }, [isSkeleton]);

  if (isSkeleton) {
    return (
      <div style={{
        backgroundColor: 'var(--color-card-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: '16px',
        padding: 'clamp(16px, 2vw, 24px)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        minHeight: 'clamp(400px, 45vh, 550px)',
        opacity: 0.5,
        filter: 'grayscale(1)',
        overflow: 'hidden'
      }}>
        <div style={{ marginBottom: 'var(--space-4)', zIndex: 10 }}>
          <div style={{ width: '100px', height: '10px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px' }} />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <canvas ref={canvasRef} style={{ width: '100%', height: '100%', maxWidth: '350px' }} />
          <div style={{ position: 'absolute', textAlign: 'center' }}>
            <div style={{ width: '80px', height: '60px', background: 'rgba(0,0,0,0.05)', borderRadius: '12px', margin: '0 auto 1rem' }} />
            <div style={{ width: '40px', height: '10px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px', margin: '0 auto' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'var(--color-card-bg)',
      border: '1px solid var(--color-border)',
      borderRadius: '16px',
      padding: 'clamp(16px, 2vw, 24px)',
      paddingBottom: 'clamp(12px, 1.5vw, 16px)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      minHeight: 'clamp(400px, 45vh, 550px)',
      maxHeight: '600px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: 'var(--space-4)',
        zIndex: 10
      }}>
        <h3 style={{
          fontFamily: 'var(--font-primary)',
          fontSize: '11px',
          fontWeight: 'var(--font-medium)',
          color: 'var(--color-muted)',
          margin: 0,
          letterSpacing: '0.8px',
          textTransform: 'uppercase'
        }}>
          AGGREGATE SCORE
        </h3>
      </div>

      {/* Canvas and Score Container */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
        overflow: 'hidden'
      }}>
        {/* Canvas - Particle Orbit */}
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(100%, 450px)',
            height: 'min(100%, 450px)',
            maxWidth: '100%',
            maxHeight: '100%',
            pointerEvents: 'none'
          }}
        />

        {/* Score Display - Centered */}
        <div style={{
          textAlign: 'center',
          zIndex: 2,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Large Score Number */}
          <div style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'clamp(48px, 8vw, 85px)',
            fontWeight: '600',
            color: 'var(--color-text)',
            lineHeight: '1',
            letterSpacing: '-2px',
            marginBottom: 'clamp(4px, 1vw, 8px)'
          }}>
            {score !== null ? score : '--'}
          </div>

          {/* Score Label */}
          <div style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'clamp(9px, 1vw, 11px)',
            fontWeight: '700',
            color: 'var(--color-primary)',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            marginBottom: 'clamp(2px, 0.5vw, 4px)'
          }}>
            {score !== null ? getScoreLabel(score) : 'NO DATA'}
          </div>

          {/* Date */}
          <div style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'clamp(8px, 0.9vw, 10px)',
            fontWeight: 'var(--font-normal)',
            color: 'var(--color-secondary)',
            opacity: 0.7
          }}>
            {getScoreDate()}
          </div>
        </div>
      </div>
    </div>
  );
};
